import { DeleteOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, Form, Button, Popconfirm, Switch, Card } from 'antd'
import { FormInstance } from 'antd/lib'
import { useParams } from 'react-router-dom'

import {
  useIdentityServiceDeleteProfileRoleEndpoint,
  useIdentityServiceGetProfileRoleListEndpointKey,
  useIdentityServiceUpdateProfileRolesEndpoint,
  useUserServiceGetUserRolesListEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { Text, RoleSelect } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  name: number
  form: FormInstance
}

export const RoleCard = ({ name, form }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const queryClient = useQueryClient()
  const { id } = useParams()

  const role = Form.useWatch(['roles', name], form)

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useIdentityServiceGetProfileRoleListEndpointKey]
    })

    queryClient.invalidateQueries({
      queryKey: [useUserServiceGetUserRolesListEndpointKey]
    })
  }

  const { mutate: remove } = useIdentityServiceDeleteProfileRoleEndpoint({
    onSuccess: onMutationSuccess
  })

  const { mutate: update, isPending } =
    useIdentityServiceUpdateProfileRolesEndpoint({
      onSuccess: onMutationSuccess,
      onError: (error: ApiError) => {
        formErrorHandler(error.body.errors)
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    update({
      id: String(id),
      requestBody: values
    })
  }

  return (
    <Card className="full-width">
      <Flex gap={4} vertical>
        <Text weight={600} size="xxl">
          {role?.legalEntityName}
        </Text>

        <Form.Item name={[name, 'roleId']}>
          <RoleSelect
            disabled
            hasDefault={false}
            className={styles.selectAsText}
          />
        </Form.Item>

        <Flex align="center" gap={8}>
          <Flex onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title={t('roles.delete_confirm_title')}
              onConfirm={() =>
                remove({
                  id: String(id),
                  roleId: role?.roleId
                })
              }
            >
              <Button icon={<DeleteOutlined />} danger ghost />
            </Popconfirm>
          </Flex>

          <Flex align="center" gap={8}>
            <Flex onClick={(e) => e.stopPropagation()}>
              <Form.Item name={[name, 'isActive']} valuePropName="checked">
                <Switch onChange={onSubmit} disabled={isPending} />
              </Form.Item>
            </Flex>

            <Text>
              {role?.isActive ? t('common.active') : t('common.inactive')}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}
